import { FieldValues } from 'react-hook-form'
import {
    GetOrders200Response,
    OrderAppointmentsResponse,
    OrderDeliveriesResponse,
    OrderDetailResponse,
    OrderMessageThreadsResponse,
    OrderPhase,
    CompleteMultipartRequest,
    CompleteUploadRequest,
    FileListResponse,
    GetOrderUploadPresignedUrl200Response,
    SingleUploadResponseUploadTypeEnum,
    PostOrderRequestInvoiceDetails,
    Product,
    ProductOption
} from '../../model'
import { del, get, post, put } from './api'

export interface ExtendedProductOption extends ProductOption {
    _isAdded?: boolean
}

export interface ExtendedProduct extends Product {
    _isPack?: boolean
    availableOptions?: ExtendedProductOption[]
    products?: ExtendedProduct[]
}

export const getOrders = async (query?: {
    searchString?: string
    phase?: OrderPhase | undefined
    page?: number
    pageSize?: number
}): Promise<GetOrders200Response> => {
    const { data } = await get('/orders', {
        params: {
            ...query
        }
    })

    return data
}

export const retrieveAddress = async (params: FieldValues) => {
    const { data } = await put('/orders/retrieve-address', {
        data: params
    })

    return data
}

export const calculateTotalOrderPrice = async (params: {
    zipcode: string
    houseNumber: number
    extension?: string
    category: number
    packs?: number[]
    products?: number[]
    productOptions?: ExtendedProductOption[]
}) => {
    const { data } = await put('/orders/calculate', {
        data: params
    })

    return data
}

export const createOrder = async (params: {
    category: number
    zipcode: string
    houseNumber: number
    extension: string
    invoiceDetails?: PostOrderRequestInvoiceDetails
    invoice3rdParty?: 'yes' | 'no'
    surface?: string
    packs?: number[]
    products?: number[]
    productOptions?: number[]
    remarks?: string
}) => {
    const { data } = await post('/orders', {
        data: params
    })

    return data
}

export const getMappedProductOptions = (data: ExtendedProduct[]) => {
    const opts: ExtendedProduct[] =
        data.length > 0
            ? data
                  .map((a: ExtendedProduct) => {
                      if (a._isPack) {
                          return a.products || []
                      } else {
                          return a
                      }
                  })
                  .flat()
            : []

    const allOptions =
        opts.length > 0 &&
        opts
            .filter((a) => a.availableOptions)
            .map((a: any) => a.availableOptions)
            .flat()
            .filter((a: any) => a._isAdded) // filter undefined values

    // get all product options that are added
    const productOptions = allOptions && allOptions.length > 0 && allOptions.map((a: any) => a.id)

    return productOptions || []
}

// ORDER DETAILS RESPONSE

export const getOrderDetails = async (orderId: number): Promise<OrderDetailResponse> => {
    const { data } = await get(`/orders/${orderId}`)
    return data
}

export const getOrderDetailMessageThreads = async (
    orderId: number,
    query?: { page?: number; pageSize?: number }
): Promise<OrderMessageThreadsResponse> => {
    const { data } = await get(`/orders/${orderId}/messageThreads`, {
        params: {
            ...query
        }
    })
    return data
}

export const getOrderDetailDeliveries = async (
    orderId: number,
    query?: { page?: number; pageSize?: number }
): Promise<OrderDeliveriesResponse> => {
    const { data } = await get(`/orders/${orderId}/deliveries`, {
        params: {
            ...query
        }
    })
    return data
}

export const getOrderDetailAppointments = async (
    orderId: number,
    query?: { page?: number; pageSize?: number }
): Promise<OrderAppointmentsResponse> => {
    const { data } = await get(`/orders/${orderId}/appointments`, {
        params: {
            ...query
        }
    })
    return data
}

export const getSuborderUploadFiles = async (orderId: number, suborderId: number): Promise<FileListResponse> => {
    const { data } = await get(`/orders/${orderId}/uploads/suborder/${suborderId}`)
    return data
}

export const getPresignedOrderDetailUploadFile = async (
    orderId: number,
    suborderId: number,
    file: File
): Promise<GetOrderUploadPresignedUrl200Response> => {
    const { data } = await post(`/orders/${orderId}/uploads/presigned-url`, {
        data: {
            fileName: file.name,
            fileSize: file.size,
            contentType: file.type || 'application/octet-stream',
            suborderId
        }
    })

    return data
}

// Upload file to S3 using the presigned URL
export const uploadToS3 = async (uploadUrl: string, file: File) => {
    const results = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
            'Content-Type': file.type
        }
    })

    return results
}

// Confirm to the backend that the upload to S3 is complete
export const confirmUploadToS3 = async (orderId: number, suborderId: number, objectKey: string, file: File) => {
    const data: CompleteUploadRequest = {
        objectKey,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type || 'application/octet-stream',
        suborderId
    }
    const results = await post(`/orders/${orderId}/uploads/complete`, {
        data
    })
    return results
}

const uploadMultipartPart = async (url: string, part: Blob, contentType: string) => {
    const response = await fetch(url, {
        method: 'PUT',
        body: part,
        headers: { 'Content-Type': contentType }
    })
    const etag = response.headers.get('ETag')
    if (!response.ok || !etag) {
        throw new Error(!response.ok ? 'Het uploaden van een bestandsonderdeel is mislukt.' : 'Geen ETag ontvangen.')
    }
    return etag
}

export const getPresignedUrlandUploadToS3Directly = async (orderId: number, suborderId: number, file: File) => {
    const upload = await getPresignedOrderDetailUploadFile(orderId, suborderId, file)

    if (upload.uploadType === SingleUploadResponseUploadTypeEnum.Single) {
        const response = await uploadToS3(upload.uploadUrl, file)
        if (!response.ok) throw new Error('Het uploaden van het bestand is mislukt.')
        return confirmUploadToS3(orderId, suborderId, upload.objectKey, file)
    }

    try {
        const parts = []
        for (const part of upload.parts) {
            const start = (part.partNumber - 1) * upload.partSize
            const etag = await uploadMultipartPart(
                part.url,
                file.slice(start, start + upload.partSize),
                file.type || 'application/octet-stream'
            )
            parts.push({ partNumber: part.partNumber, etag })
        }
        const data: CompleteMultipartRequest = {
            objectKey: upload.objectKey,
            uploadId: upload.uploadId,
            suborderId,
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type || 'application/octet-stream',
            parts
        }
        return post(`/orders/${orderId}/uploads/complete-multipart`, { data })
    } catch (error) {
        try {
            await post(`/orders/${orderId}/uploads/abort-multipart`, {
                data: { multipartUploadId: upload.uploadId, objectKey: upload.objectKey }
            })
        } catch (_) {}
        throw error
    }
}

export const deleteOrderDetailUploadFile = async (orderId: number, fileId: string) => {
    const { data } = await del(`/orders/${orderId}/uploads/${fileId}`)
    return data
}

export const downloadOrderDetailUploadFile = async (orderId: number, fileId: string) => {
    const { data } = await get(`/orders/${orderId}/uploads/${fileId}/download`)
    return data
}
